import { useEffect, useRef, useState } from 'react';
import { geocode } from '../lib/api';
import type { Origin } from '../lib/eventFilters';

// Distance is stored as fractional miles in URL/localStorage for backward
// compatibility, but the UI is exposed in yards for fine-grained tuning.
const YARDS_PER_MILE = 1760;
const YARDS_MIN = 50;
const YARDS_MAX = 50000; // ~28.4 mi
const YARDS_STEP = 50;
const DEFAULT_YARDS = 880; // 0.5 mi — sensible default for a "yards" mental model

const milesToYards = (mi: number | undefined): number | undefined =>
  typeof mi === 'number' && mi > 0 ? Math.round(mi * YARDS_PER_MILE) : undefined;
const yardsToMiles = (yd: number | undefined): number | undefined =>
  typeof yd === 'number' && yd > 0 ? yd / YARDS_PER_MILE : undefined;
const formatYards = (yd: number): string => yd.toLocaleString();

export interface DistanceControlProps {
  origin: Origin | undefined;
  maxDistanceMiles: number | undefined;
  onChange: (next: { origin?: Origin; maxDistanceMiles?: number }) => void;
  /** Optional id prefix for input/label pairs when more than one control is rendered on the same page. */
  idPrefix?: string;
}

/**
 * Reusable address + max-distance pill used by the events, venues, and series
 * pages. The component is purely presentational: it does not own any state
 * beyond the popover/draft UI \u2014 callers persist `origin` and
 * `maxDistanceMiles` themselves (see {@link useStoredOrigin}).
 *
 * UX:
 *   - Compact pill that shows the current address (or "Set address\u2026" when empty).
 *   - Clicking opens a popover with an address input (server-side geocoded via
 *     /api/geocode) and a yards-based distance slider + number input for fine
 *     control.
 *   - Setting a brand-new origin defaults the radius to 880 yards (0.5 mi) when
 *     none is set.
 */
export default function DistanceControl({
  origin,
  maxDistanceMiles,
  onChange,
  idPrefix = 'mh-distance',
}: DistanceControlProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(origin?.label ?? '');
  const [status, setStatus] = useState<'idle' | 'looking' | 'notfound' | 'error'>('idle');
  const ref = useRef<HTMLDivElement>(null);

  // Keep draft in sync when the origin is replaced externally (URL load, hook seeding).
  useEffect(() => {
    setDraft(origin?.label ?? '');
  }, [origin?.label]);

  // Click-outside / Esc to close, mirroring the SavedViewsMenu/MultiSelect patterns.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const resolveAddress = async (label: string) => {
    const q = label.trim();
    if (!q) return;
    setStatus('looking');
    try {
      const r = await geocode(q);
      if (!r) {
        setStatus('notfound');
        return;
      }
      setStatus('idle');
      // Default to 880 yd (0.5 mi) the first time an origin is set so the
      // filter is immediately useful.
      onChange({
        origin: { label: q, lat: r.lat, lng: r.lng },
        maxDistanceMiles: maxDistanceMiles ?? yardsToMiles(DEFAULT_YARDS),
      });
    } catch {
      setStatus('error');
    }
  };

  const clearOrigin = () => {
    onChange({ origin: undefined, maxDistanceMiles: undefined });
    setDraft('');
    setStatus('idle');
  };

  const setRadius = (miles: number | undefined) =>
    onChange({ origin, maxDistanceMiles: miles });
  const setRadiusYards = (yd: number | undefined) => setRadius(yardsToMiles(yd));

  const currentYards = milesToYards(maxDistanceMiles);
  const triggerLabel = origin
    ? `${currentYards ? `${formatYards(currentYards)} yd` : 'Any distance'} from ${origin.label}`
    : 'Set address\u2026';
  const triggerShort = origin
    ? `${currentYards ? `${formatYards(currentYards)} yd` : 'Any'} from ${origin.label}`
    : 'Set address\u2026';
  const addressId = `${idPrefix}-address`;
  const radiusId = `${idPrefix}-radius`;
  const radiusSliderId = `${idPrefix}-radius-slider`;

  return (
    <div ref={ref} className="position-relative">
      <button
        type="button"
        className={`mh-toggle ${origin ? 'mh-toggle--on' : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
        title={triggerLabel}
      >
        <i className="bi bi-geo-alt" aria-hidden="true" />{' '}
        <span className="mh-distance__label">{triggerShort}</span>
        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} ms-1`} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Filter by distance"
          className="mh-distance__panel card shadow-sm position-absolute mt-1 p-3"
        >
          <label htmlFor={addressId} className="form-label small fw-semibold mb-1">
            Address or place
          </label>
          <div className="d-flex gap-1">
            <input
              id={addressId}
              type="text"
              className="form-control form-control-sm"
              placeholder="e.g. 1257 Glen Laura Road"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void resolveAddress(draft);
                }
              }}
            />
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => void resolveAddress(draft)}
              disabled={status === 'looking' || !draft.trim()}
            >
              {status === 'looking' ? '…' : 'Set'}
            </button>
          </div>
          {status === 'notfound' && (
            <div className="form-text text-danger" role="alert">
              Couldn’t find that address. Try adding a city.
            </div>
          )}
          {status === 'error' && (
            <div className="form-text text-danger" role="alert">
              Geocoding failed. Try again.
            </div>
          )}
          {origin && (
            <div className="form-text">
              Using <strong>{origin.label}</strong>
            </div>
          )}

          <hr className="my-2" />

          <label htmlFor={radiusId} className="form-label small fw-semibold mb-1">
            Max distance (yards)
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              id={radiusId}
              type="number"
              inputMode="numeric"
              className="form-control form-control-sm mh-distance__yards-input"
              min={YARDS_MIN}
              max={YARDS_MAX}
              step={YARDS_STEP}
              value={currentYards ?? ''}
              disabled={!origin}
              placeholder="Any"
              aria-describedby={`${radiusId}-help`}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setRadiusYards(undefined);
                  return;
                }
                const n = Number(raw);
                if (!Number.isFinite(n) || n <= 0) {
                  setRadiusYards(undefined);
                  return;
                }
                setRadiusYards(Math.min(YARDS_MAX, Math.max(YARDS_MIN, Math.round(n))));
              }}
            />
            <span className="small text-muted">yd</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-auto"
              onClick={() => setRadiusYards(undefined)}
              disabled={!origin || currentYards === undefined}
              title="Remove distance limit"
            >
              Any
            </button>
          </div>
          <input
            id={radiusSliderId}
            type="range"
            className="form-range mt-2"
            min={YARDS_MIN}
            max={YARDS_MAX}
            step={YARDS_STEP}
            value={currentYards ?? DEFAULT_YARDS}
            disabled={!origin}
            aria-label="Max distance in yards"
            onChange={(e) => setRadiusYards(Number(e.target.value))}
          />
          <div id={`${radiusId}-help`} className="form-text">
            {currentYards
              ? `${formatYards(currentYards)} yd (~${(currentYards / YARDS_PER_MILE).toFixed(2)} mi)`
              : 'No distance limit applied.'}
          </div>

          <div className="d-flex justify-content-between mt-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={clearOrigin}
              disabled={!origin}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
